import { useMultiRootEditor } from '@ckeditor/ckeditor5-react';
import { Editor, MultiRootEditor } from 'ckeditor5';
import { useTranslation } from 'react-i18next';

import {
  QBox,
  QFlex,
  QSpacer,
  QStack,
  useCurrentUser,
  useToastProvider,
} from '@qualio/ui-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { changeControlApi } from '../../../../api/changeControl';
import { multiRootEditorReadyHandler } from '../../../../config/handlers/EditorEventsHandlers';
import { getEditorConfigMultiRoot } from '../../../../config/MultiRootEditorConfig';
import { useAnnotationHighlighting } from '../../../../hooks/AnnotationHighlighting';
import { useElementIsRendered } from '../../../../hooks/ElementRenderedCheck';
import { ChangeControlDTO } from '../../../../types/DocumentCreate';
import { CKEditorUtils } from '../../../../util/CKEditorUtils';
import {
  hasEditorFinishedEditing,
  isOwner,
} from '../../../../util/CurrentUser';
import { getRevisionHistoryEditClass } from '../../../../util/EditModeUtils';
import {
  reflowSidebar,
  refreshDisplayMode,
} from '../../../../util/SidebarUtils';
import { BackToTop } from '../../BackToTop';
import { ToolbarWrapper } from '../../ToolbarWrapper/ToolbarWrapper';
import {
  DocumentOverviewContext,
  EditorStatus,
  EditorStatusContext,
} from '../Context';
import { HTMLViewer } from '../HTMLViewer';
import { CommentButtons } from '../QMultiRootEditor/CommentButtons';
import { CommentSidebar } from '../QMultiRootEditor/CommentSidebar';
import { QMultiRootEditorContext } from '../QMultiRootEditor/Context';
import { OutlineSidebar } from '../QMultiRootEditor/OutlineSidebar';
import styles from '../QMultiRootEditor/QMultiRootEditor.module.css';
import { QMultiRootEditorLayout } from '../QMultiRootEditor/QMultiRootEditorLayout';
import { RevisionHistorySidebar } from '../QMultiRootEditor/RevisionHistorySidebar';
import { canUserEditDocument, canUserSuggestOnDocument } from '../RenderLogic';

export const QChangeControlMultiRootEditor = ({
  handleAttachmentClick,
  handleSessionDisconnectionEvent,
  setEditors,
}: {
  handleAttachmentClick: (attachmentId: string) => void;
  handleSessionDisconnectionEvent: (didReceive: boolean) => void;
  setEditors: any;
}) => {
  const { t } = useTranslation();
  const { setCCEditorStatus } = useContext(EditorStatusContext);
  const { setCCEditor } = useContext(QMultiRootEditorContext);
  const { handleHighlighting } = useAnnotationHighlighting();
  const { showToast } = useToastProvider();

  const [searchParams] = useSearchParams();
  const targetId = searchParams.get('targetId') ?? undefined;
  const location = useLocation();
  const editorContainerRef = useRef(null);
  const editorInstanceRef = useRef<MultiRootEditor | null>(null);

  const editorRevisionHistoryRef = useRef(null);
  const editorRevisionHistoryEditorRef = useRef(null);
  const [isLayoutReady, setIsLayoutReady] = useState(false);

  const [, sidebarElement] = useElementIsRendered('#changeControlSidebar');
  const [, ccPresenceListContainer] = useElementIsRendered('.cc-presence-list');

  const [, docRevisionHistorySidebar] = useElementIsRendered(
    '#changeControlSidebar-revision-history',
  );

  const [, editorToolbarRef] = useElementIsRendered('#cc-editor-toolbar');

  useEffect(() => {
    setIsLayoutReady(true);

    return () => setIsLayoutReady(false);
  }, []);

  useEffect(() => {
    setCCEditorStatus(EditorStatus.CONNECTING);

    return () => {
      setCCEditorStatus(EditorStatus.STABLE);
    };
  }, [setCCEditorStatus]);

  const {
    changeControl,
    qualioDocument,
    commentsPermission,
    users,
    tags,
    collaborationToken,
  } = useContext(DocumentOverviewContext);
  const currentUser = useCurrentUser();
  const {
    additionalPremiumPlugins,
    usePagination,
    useRevisionHistory,
    editorBundleEnabled,
  } = useFlags();

  const aiEnabled = currentUser.company.ai_enabled;

  const multiRootDefaultDataAll = {} as any;
  const multiRootDefaultRootsAttributesAll = {} as any;
  changeControl.sections.forEach((section, index) => {
    multiRootDefaultDataAll[`${section.id}-header`] =
      `<h1 >${section.position}. ${section.title} </h1>`;
    multiRootDefaultDataAll[`${section.id}`] = section.content;
    multiRootDefaultRootsAttributesAll[`${section.id}-header`] = {
      order: section.position + 2,
      isHeader: true,
      rootName: section.id + '-header',
    };
    multiRootDefaultRootsAttributesAll[`${section.id}`] = {
      order: section.position + 3,
      isHeader: false,
      rootName: section.id,
    };
  });

  const rootsContent = multiRootDefaultDataAll;
  const rootsAttributes = multiRootDefaultRootsAttributesAll;

  const autoSaveChangeControl = useCallback(
    async (editor: Editor) => {
      if (commentsPermission <= 1) {
        return;
      }
      let currentDocumentData: Record<string, string>;
      if (useRevisionHistory) {
        const revisionTracker = editor.plugins.get('RevisionTracker') as any;
        const currentRevision = revisionTracker.currentRevision;

        // Update the current revision with the newest document changes.
        await revisionTracker.update();
        currentDocumentData =
          await revisionTracker.getRevisionDocumentData(currentRevision);
      } else {
        currentDocumentData = (editor as MultiRootEditor).getFullData();
      }

      const cloudSectionVersion = CKEditorUtils.getCloudDocumentVersion(editor);
      const updatedSections = Object.entries(currentDocumentData).reduce(
        (acc: ChangeControlDTO['sections'], revisionData) => {
          const [id, content] = revisionData;
          const originalSection = changeControl.sections.find(
            (section) => section.id === Number(id),
          );
          if (originalSection) {
            const updatedSection = {
              ...originalSection,
              content: String(content),
              type: 'text',
              cloud_section_version: Number(cloudSectionVersion),
            };
            acc.push(updatedSection);

            originalSection.content = updatedSection.content;
            originalSection.type = updatedSection.type;
            originalSection.cloud_section_version =
              updatedSection.cloud_section_version;
          }
          return acc;
        },
        [],
      );

      const autosavePayload = {
        ...changeControl,
        content_last_modifed_at: Date.now() / 1000,
        sections: updatedSections,
      };
      await changeControlApi
        .updateChangeControl(autosavePayload, false)
        .then(() => {
          setCCEditorStatus(EditorStatus.SAVED);
        })
        .catch((e) => {
          setCCEditorStatus(EditorStatus.ERROR);
          showToast({
            'data-cy': 'change-control-save-error-toast',
            id: 'change-control-save-error',
            status: 'error',
            title: t('notifications.error'),
            description: t('errors.errorSavingChangeControl', {
              error: e.message,
            }),
            replace: true,
          });
        });
      return Promise.resolve();
    },
    // Do not add showToast to this array, it will crash the editor mid render
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [changeControl, setCCEditorStatus, commentsPermission],
  );

  const editorConfig = useMemo(() => {
    return getEditorConfigMultiRoot({
      companyId: qualioDocument.company_id,
      collaborationToken,
      sidebarConfig: {
        sidebarElement,
        preventScrollOutOfView: false,
      },
      presenceListConfig: {
        containerElement: ccPresenceListContainer,
      },
      editorAiEnabled: aiEnabled,
      qualioDocument,
      users,
      tags,
      handleAutoSave: autoSaveChangeControl,
      commentsPermission,
      collaborationEntityId: changeControl.id.toString(),
      collaborationEntityName: 'change-control',
      documentId: qualioDocument.id,
      documentStatusId: qualioDocument.status_id,
      documentOwner: qualioDocument.owner,
      entityType: 'change_control',
      editorContainerRef: editorContainerRef,
      editorRevisionHistoryRef: editorRevisionHistoryRef,
      editorRevisionHistoryEditorRef: editorRevisionHistoryEditorRef,
      docRevisionHistorySidebar: docRevisionHistorySidebar,
      editorInstanceRef: editorInstanceRef,
      editorToolbarRef: editorToolbarRef,
      additionalPremiumPlugins,
      usePagination,
      currentUser,
      useRevisionHistory,
      attachmentPreview: handleAttachmentClick,
      editorBundleVersion: editorBundleEnabled
        ? process.env.REACT_APP_EDITOR_BUNDLE_VERSION
        : undefined,
    });
  }, [
    qualioDocument,
    changeControl,
    collaborationToken,
    sidebarElement,
    ccPresenceListContainer,
    aiEnabled,
    users,
    tags,
    autoSaveChangeControl,
    commentsPermission,
    docRevisionHistorySidebar,
    editorToolbarRef,
    additionalPremiumPlugins,
    usePagination,
    currentUser,
    useRevisionHistory,
    handleAttachmentClick,
    editorBundleEnabled,
  ]);

  const editorProps = {
    isLayoutReady,
    editor: MultiRootEditor,
    data: rootsContent,
    rootsAttributes,
    config: editorConfig as Record<string, unknown>,
    onError: (error: any) => {
      console.error(error);
    },
    disableTwoWayDataBinding: true,
    disableWatchdog: true,
    onReady: (editor: MultiRootEditor) => {
      editorInstanceRef.current = editor;
      setCCEditor(editor);
      setEditors([editor]);
      const roots = editor.getRootsAttributes();
      Object.keys(roots).forEach((key: string) => {
        const root = roots[key];
        if (root.isHeader) {
          editor.disableRoot(root.rootName as string, 'header');
        }
      });
      multiRootEditorReadyHandler(
        editor,
        'changeControlEditorContent',
        currentUser,
        commentsPermission,
        'changeControlCommentControl',
        qualioDocument.orientation,
        qualioDocument.status_id,
        setCCEditorStatus,
        handleHighlighting,
        handleAttachmentClick,
        qualioDocument.id,
        handleSessionDisconnectionEvent,
        targetId,
        false,
        hasEditorFinishedEditing(currentUser, qualioDocument),
        isOwner(currentUser, qualioDocument),
      );
    },
  };

  const { editableElements, toolbarElement, attributes, editor } =
    useMultiRootEditor(editorProps);

  // Sorted elements based on order attribute.
  const sortedElements = editableElements.sort((a, b) => {
    const orderA = Number(attributes[a.props.id]?.order ?? 0);
    const orderB = Number(attributes[b.props.id]?.order ?? 0);
    return orderA - orderB;
  });

  useEffect(() => {
    reflowSidebar([editor]);
    const dialog = editorInstanceRef.current?.plugins.get('Dialog') as any;
    if (dialog?.isOpen && dialog.id === 'commentsArchive') {
      dialog.hide();
    }
  }, [location, editor]);

  useEffect(() => {
    const tabPanels = document.getElementById('document-overview-tab-panels');
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentBoxSize) {
          refreshDisplayMode({
            instances: [editor],
            windowWidth: entries[0].borderBoxSize[0].inlineSize ?? undefined,
            breakpointOverride: 1500,
          });
        }
      }
    });
    if (tabPanels) {
      observer.observe(tabPanels);
    }
    return () => {
      if (tabPanels) {
        observer.unobserve(tabPanels);
      }
    };
  }, [editor]);

  const showToolbar = useMemo(() => {
    return (
      canUserEditDocument(qualioDocument, currentUser) ||
      canUserSuggestOnDocument(qualioDocument, currentUser)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    qualioDocument.status_id,
    qualioDocument.owner.id,
    qualioDocument.audit_groups?.editor_group,
    currentUser.userId,
  ]);

  return (
    <>
      {showToolbar && (
        <ToolbarWrapper
          className={`${
            styles['document-overview__toolbar']
          } ${getRevisionHistoryEditClass(qualioDocument)}`}
          id="cc-editor-toolbar"
        >
          {toolbarElement}
        </ToolbarWrapper>
      )}
      {!showToolbar && (
        <QBox style={{ display: 'none' }}>{toolbarElement}</QBox>
      )}
      <QFlex
        flexShrink={1}
        flexGrow={1}
        justifyContent="space-between"
        bg="gray.50"
        border="1px"
        borderTop={showToolbar ? 'none' : '1px'}
        borderRadius={showToolbar ? '0 0 4px 4px' : '4px'}
        borderColor="gray.200"
        className={`${
          styles['document-content-container']
        } editor-container ${getRevisionHistoryEditClass(qualioDocument)} ${
          commentsPermission < 2 ? 'non-editable-mode' : ''
        }`}
        overflowY="hidden"
        overflowX="hidden"
        ref={editorContainerRef}
      >
        <OutlineSidebar
          editor={editor}
          scrollBoundaryRef={editorContainerRef}
          toolbarElementId="cc-editor-toolbar"
          outlineClassSuffix="change_control"
          sections={changeControl.sections}
          sectionTitlesVisible={true}
          editorName="changeControlEditorContent"
        />

        <QStack direction="row" id="change-control-container">
          <QMultiRootEditorLayout contentId="changeControlEditorContent">
            <QBox className="editor-container_classic-editor editor-container_include-annotations">
              <QBox className="editor-container__editor-wrapper">
                <QBox className="editor-container__editor">
                  {!editor && <HTMLViewer sections={changeControl.sections} />}
                  <QBox className={`${editor ? '' : 'ck-hidden'}`}>
                    {sortedElements.map((element, index) => (
                      <QBox key={element.props.id}>
                        {index % 2 === 1 && (
                          <h1
                            key={`section-heading-${element.props.id}`}
                            style={{
                              height: '0px !important',
                              padding: '0px !important',
                              margin: '0px !important',
                            }}
                            className="section-heading fake"
                          >
                            <></>
                          </h1>
                        )}
                        {element}
                      </QBox>
                    ))}
                  </QBox>
                </QBox>
              </QBox>
            </QBox>
          </QMultiRootEditorLayout>
          <CommentButtons id="changeControlCommentControl" />
        </QStack>
        {/* Sidebars live OUTSIDE of the editor container */}
        <CommentSidebar id="changeControlSidebar" />
      </QFlex>
      {useRevisionHistory && (
        <QFlex
          flexShrink={1}
          flexGrow={1}
          justifyContent="space-between"
          bg="gray.50"
          border="1px"
          borderTop={showToolbar ? 'none' : '1px'}
          borderColor="gray.200"
          borderRadius={showToolbar ? '0 0 4px 4px' : '4px'}
          display="none"
          className={`${
            styles['document-content-container']
          } ${getRevisionHistoryEditClass(qualioDocument)}`}
          overflowY="hidden"
          overflowX="hidden"
          ref={editorRevisionHistoryRef}
        >
          <QFlex
            direction="column"
            flexShrink={1}
            flexGrow={1}
            className={`${styles['document-toc-container']}`}
            style={{
              minWidth: '0px',
            }}
          >
            <QSpacer height="100%" />
            <BackToTop parentRef={editorContainerRef} />
          </QFlex>

          <QStack
            direction="row"
            id="change-control-revision-history-container"
          >
            <QMultiRootEditorLayout contentId="changeControlEditorContent">
              <QBox className="revision-history" ref={editorRevisionHistoryRef}>
                <QBox className="revision-history__wrapper">
                  <QBox
                    className="revision-history__editor"
                    ref={editorRevisionHistoryEditorRef}
                    id="cc-editor-revision-history-editor"
                  />
                </QBox>
              </QBox>
            </QMultiRootEditorLayout>
          </QStack>
          {/* Sidebars live OUTSIDE of the editor container */}
          <RevisionHistorySidebar id="changeControlSidebar-revision-history" />
        </QFlex>
      )}
    </>
  );
};
